'use client';

import { usePathname, useRouter } from 'next/navigation';
import { createContext, useContext, useTransition, ReactNode, useEffect } from 'react';

interface LoadingContextType {
  isPending: boolean;
  startLoadingTransition: (callback: () => void) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const pathname = usePathname();

  // Query is read from window.location: useSearchParams() here would turn every page into
  // client-side rendering once pages are statically generated
  useEffect(() => {
    const normalizedPath = pathname.replace(/\/{2,}/g, '/');

    if (normalizedPath !== pathname) {
      const query = window.location.search;
      router.replace(`${normalizedPath}${query}`, { scroll: false });
    }
  }, [pathname, router]);

  return (
    <LoadingContext.Provider value={{ isPending, startLoadingTransition: startTransition }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
